
// js/common/media/base-media.js
import { inView as inView6 } from "vendor";
var BaseMedia = class extends HTMLElement {
  static get observedAttributes() {
    return ["playing"];
  }
  connectedCallback() {
    this._abortController = new AbortController();
    if (this.hasAttribute("autoplay")) {
      inView6(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get playing() {
    return this.hasAttribute("playing");
  }
  get player() {
    return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this._playerHandler(target, prop);
        };
      }
    });
  }
  play() {
    if (!this.playing) {
      this.player.play();
    }
  }
  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));
        if (this.hasAttribute("group")) {
          Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
            itemToPause.pause();
          });
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
      }
    }
  }
};

// js/common/media/model.js
var ModelMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    this.player;
  }
  _playerTarget() {
    return new Promise((resolve) => {
      this.setAttribute("loaded", "");
      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            const modelViewer = this.querySelector("model-viewer");
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => this.setAttribute("playing", ""));
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => this.removeAttribute("playing"));
            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
          }
        }
      ]);
    });
  }
  _playerHandler(target, prop) {
    target[prop]();
  }
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
    } else {
      const models = (await ProductLoader.load(this.getAttribute("handle")))["media"].filter((media) => media["media_type"] === "model");
      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
    }
  }
};
if (!window.customElements.get("model-media")) {
  window.customElements.define("model-media", ModelMedia);
}

// js/common/media/video.js
import { inView as inView7 } from "vendor";
var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
var VideoMedia = class extends BaseMedia {
  #mustRemoveControlsAfterSuspend = false;
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("autoplay")) {
      this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
    }
    if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(document.getElementById("video-media-default-template").content.cloneNode(true));
    }
    if (this.getAttribute("type") === "video") {
      inView7(this, () => {
        this.querySelector("video")?.setAttribute("preload", "metadata");
      }, { margin: "800px" });
    }
  }
  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute("autoplay") || matchesMediaQuery("md-max");
        const script = document.createElement("script");
        script.type = "text/javascript";
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              "onReady": () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              "onStateChange": (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("playing", "");
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("playing");
                }
              }
            }
          });
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on("play", () => {
            this.setAttribute("playing", "");
          });
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this.setAttribute("loaded", "");
      videoElement.addEventListener("play", () => {
        this.setAttribute("playing", "");
        this.removeAttribute("suspended");
        if (this.#mustRemoveControlsAfterSuspend) {
          videoElement.controls = false;
        }
      });
      videoElement.addEventListener("pause", () => {
        if (!videoElement.seeking && videoElement.paused) {
          this.removeAttribute("playing");
        }
      });
      return videoElement;
    }
  }
  _playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            if (!this.hasAttribute("controls")) {
              this.#mustRemoveControlsAfterSuspend = true;
              target.controls = true;
            }
          }
        });
      } else {
        target[prop]();
      }
    }
  }
};
if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}



function addLogoToLanguagePopup(nodeAppendAfter) {

  if (document.querySelector('#modal-logo')) document.querySelector('#modal-logo').remove();

  const logoElement = document.createElement('div');
  logoElement.id = 'modal-logo';

  const logoHTML = `<div class="icon">
  <svg width="67" height="24" viewBox="0 0 67 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M18.3516 2.87073H40.135V0H5.73896C4.2169 0 2.75717 0.604643 1.68091 1.68091C0.604643 2.75717 0 4.2169 0 5.73896V18.261C0 19.7831 0.604643 21.2428 1.68091 22.3191C2.75717 23.3954 4.2169 24 5.73896 24H66.6082V21.1292H18.3516C17.6403 21.1292 16.9581 20.8467 16.4551 20.3437C15.9521 19.8407 15.6696 19.1585 15.6696 18.4472V5.55278C15.6696 5.20057 15.7389 4.85181 15.8737 4.52641C16.0085 4.20101 16.2061 3.90534 16.4551 3.65629C16.7042 3.40724 16.9998 3.20968 17.3252 3.0749C17.6506 2.94011 17.9994 2.87073 18.3516 2.87073Z" fill="black"></path>
  <path d="M41.3594 0.367188H43.1206V0.694263H42.4312V2.53093H42.0538V0.694263H41.3694L41.3594 0.367188Z" fill="black"></path>
  <path d="M43.4141 0.367188H43.95L44.5463 2.06296L45.1274 0.367188H45.6558V2.53093H45.296V0.862829L44.6972 2.53093H44.3852L43.7839 0.862829V2.53093H43.4216L43.4141 0.367188Z" fill="black"></path>
  </svg>
  </div>`;
  logoElement.innerHTML = logoHTML;

  nodeAppendAfter.parentNode.insertBefore(logoElement, nodeAppendAfter.nextSibling);
}


document.addEventListener('DOMContentLoaded', () => {

  let intervalFlag = setInterval(() => {
    let modalCloseButtonContainer = document.querySelector('.recommendation-modal__close-button-container');

    if (modalCloseButtonContainer) {
      addLogoToLanguagePopup(modalCloseButtonContainer);
      clearInterval(intervalFlag);

      const parentElement = document.querySelector('body');
      parentElement.addEventListener('click', (event) => {
        if (event.target.classList.contains('recommendation-modal__button--minimal')) {
          modalCloseButtonContainer = document.querySelector('.recommendation-modal__close-button-container');
          addLogoToLanguagePopup(modalCloseButtonContainer);
        }
      });

    }
  }, 100);
})


// Hide product images until are completely loaded
document.querySelectorAll('article.product')?.forEach((element) => {
  element.classList.add('hide');
});
document.querySelectorAll('.product img:not(.image-hover)')?.forEach((element) => {
  const img = new Image();
  img.src = element.src;

  img.onload = function () {
    element.closest('article.product').classList.remove('hide');
  }
})

document.addEventListener('DOMContentLoaded', () => {
  
  // Change locale selectors default position
  const observerLocaleSelector = new MutationObserver(() => {
    var newStyle = document.createElement('style');
    newStyle.innerHTML = "#country_code { font-size: 13px !important; }";
    
    // Agrega el nuevo estilo al head del documento
    document.head.appendChild(newStyle);    
    const localSelectorContainer = document.querySelector('.locale-selectors__container');
    if (localSelectorContainer) {
      localSelectorContainer.style.display = "flex";
      localSelectorContainer.style.alignItems = "end";
    }
    const footerTop = document.querySelector('.footer__top');

    if (localSelectorContainer && footerTop) {
      footerTop.appendChild(localSelectorContainer);
      observerLocaleSelector.disconnect();
    }
  });

  const targetNode = document.body;
  const config = { childList: true, subtree: true };  // Configure the observer to watch for subtree changes
  observerLocaleSelector.observe(targetNode, config);


  // Top bar close event listener
  var bannerClose = document.querySelector(".banner-close");
  if (bannerClose) {
    bannerClose.addEventListener('click', () => {
      document.querySelector('body').classList.add('banner-hidden');
    })
  }

});
